/* istanbul ignore file */

/*
TODO:
  1. fix test coverage -- we're still expecting to use a JSON definition for building the form, so testing, validation etc is tbc
  2. validation - still heading for JSON defs, but no complex logic anticipated yet
  3. use json metadata to build form (hence form not divided into sensible components yet)
*/
import type { MouseEvent } from 'react';
import React, { useCallback, useRef, useState } from 'react';
import { useEvent } from 'react-use';
import type { ValidationRules } from '@noths/polaris-client-react-hooks';
import { useForm } from '@noths/polaris-client-react-hooks';

import { ApplyToSellWithUsContainer } from 'src/components/molecules/ApplyToSellWithUsContainer/ApplyToSellWithUsContainer';
import * as track from 'src/hooks/useTracking/tracking';
import { initialFormData, submitOnboardingApplication } from 'src/services/post-form-data';
import { getFeatureFlags } from 'src/settings';
import type { RegistrationForm } from 'src/types/registration';
import { AttachmentState } from 'src/types/registration';
import { logError } from 'src/utils/logError';
import { AboutYou, MIN_COMPANY_NAME_LENGTH } from './components/AboutYou/AboutYou';
import { AboutYourBusiness } from './components/AboutYourBusiness/AboutYourBusiness';
import { AboutYourProducts } from './components/AboutYourProducts/AboutYourProducts';
import { ApplicationError } from './components/ApplicationError';
import { ApplicationSubmitted } from './components/ApplicationSubmitted';
import {
  MUST_BE_LONGER_THAN_3_CHARACTERS_ERROR,
  MUST_BE_TICKED_ERROR,
  MUST_NOT_BE_EMPTY_ERROR,
  MUST_NOT_EXCEED_250_ERROR,
  MUST_NOT_EXCEED_500_ERROR,
  MUST_SELECT_AT_LEAST_ONE_ERROR,
  MUST_SELECT_ONE_ERROR,
  MUST_SELECT_YES_NO_ERROR,
  MUST_UPLOAD_AT_LEAST_ONE_FILE_ERROR,
  OPTION_MUST_BE_SELECTED_ERROR,
  UPLOAD_IN_PROGRESS_ERROR,
} from './constants/copy';
import type { FormValidations } from './types';

export interface SignupProps {
  data?: RegistrationForm;
  section?: number;
}

const LAST_PAGE = 3;

export const MAX_LENGTH_250_VALIDATION_RULE: ValidationRules<RegistrationForm>['maxLength'] = {
  value: 250,
  message: MUST_NOT_EXCEED_250_ERROR,
};

export const MAX_LENGTH_500_VALIDATION_RULE: ValidationRules<RegistrationForm>['maxLength'] = {
  value: 500,
  message: MUST_NOT_EXCEED_500_ERROR,
};

export const Signup = ({ data = initialFormData, section = 0 }: SignupProps) => {
  const uploadSectionsEnabled =
    getFeatureFlags().uploadSectionsEnabled || window.localStorage.getItem('forceUploadSections');

  // @ts-ignore 2740 -- expecting spurious root fields for address.* telephone.* etc
  const validations: FormValidations = {
    'address.country': {
      isRequired: { value: true, message: OPTION_MUST_BE_SELECTED_ERROR },
    },
    'address.addressLine1': {
      maxLength: MAX_LENGTH_250_VALIDATION_RULE,
      isRequired: { value: true, message: MUST_NOT_BE_EMPTY_ERROR },
    },
    'address.addressLine2': {
      maxLength: MAX_LENGTH_250_VALIDATION_RULE,
    },
    'address.addressTown': {
      maxLength: MAX_LENGTH_250_VALIDATION_RULE,
      isRequired: { value: true, message: MUST_NOT_BE_EMPTY_ERROR },
    },
    'address.postcode': {
      maxLength: MAX_LENGTH_250_VALIDATION_RULE,
      isRequired: { value: true, message: MUST_NOT_BE_EMPTY_ERROR },
    },
    'certification.required': {
      isRequired: { value: true, message: MUST_SELECT_ONE_ERROR },
    },
    companyName: {
      minLength: {
        value: MIN_COMPANY_NAME_LENGTH,
        message: MUST_BE_LONGER_THAN_3_CHARACTERS_ERROR,
      },
      maxLength: MAX_LENGTH_250_VALIDATION_RULE,
      isRequired: { value: true, message: MUST_NOT_BE_EMPTY_ERROR },
    },
    'deliverAreas.uk': {
      validate: {
        value: (val, state) => {
          if (state.deliverAreas.uk === true) {
            clearFormError('deliverAreas.selfDeliveryAreas');
            clearFormError('deliverAreas.selfDelivery');
          }
          return state.deliverAreas.uk === undefined;
        },
        message: MUST_SELECT_YES_NO_ERROR,
      },
    },
    'deliverAreas.selfDelivery': {
      validate: {
        value: (val, state) => {
          const isNotDeliveringToUK = state.deliverAreas.uk === false;
          const hasNoOptionSelected = val === undefined;

          return isNotDeliveringToUK && hasNoOptionSelected;
        },
        message: MUST_SELECT_YES_NO_ERROR,
      },
    },
    'deliverAreas.selfDeliveryAreas': {
      maxLength: MAX_LENGTH_500_VALIDATION_RULE,
      validate: {
        value: (_val, state) => {
          return state.deliverAreas.uk === false && !state.deliverAreas.selfDeliveryAreas.length;
        },
        message: MUST_NOT_BE_EMPTY_ERROR,
      },
    },
    instagramHandle: {
      maxLength: MAX_LENGTH_250_VALIDATION_RULE,
    },
    leadinTime: {
      isRequired: { value: true, message: MUST_SELECT_ONE_ERROR },
    },
    partnerSource: {
      isRequired: { value: true, message: OPTION_MUST_BE_SELECTED_ERROR },
    },
    registrationType: {
      isRequired: { value: true, message: MUST_SELECT_ONE_ERROR },
    },
    ...(uploadSectionsEnabled && {
      packagingAttachments: {
        validate: {
          value: (val, state) => !state.packagingAttachments.length,
          message: MUST_UPLOAD_AT_LEAST_ONE_FILE_ERROR,
        },
      },
    }),
    'packagingAttachments.notUploaded': {
      validate: {
        value: (_val, state) => {
          return state.packagingAttachments.some(
            (attachment) => attachment.attachmentState === AttachmentState.UPLOADING,
          );
        },
        message: UPLOAD_IN_PROGRESS_ERROR,
      },
    },
    partnerAgreesToProvideId: {
      isRequired: { value: true, message: MUST_BE_TICKED_ERROR },
    },
    productCategories: {
      validate: {
        value: (val, state) => !state.productCategories.length,
        message: MUST_SELECT_AT_LEAST_ONE_ERROR,
      },
    },
    'pipelinePlans.provided': {
      validate: {
        value: (val) => {
          if (val === false) {
            clearFormError('pipelinePlans.details');
          }
          return val === undefined;
        },
        message: MUST_SELECT_YES_NO_ERROR,
      },
    },
    'pipelinePlans.details': {
      maxLength: MAX_LENGTH_500_VALIDATION_RULE,
      validate: {
        value: (val, state) => {
          if (state.pipelinePlans.provided) {
            return `${val}`.trim() === '';
          }
          return false;
        },
        message: MUST_NOT_BE_EMPTY_ERROR,
      },
    },
    'telephone.dialingCode': {
      isRequired: { value: true, message: OPTION_MUST_BE_SELECTED_ERROR },
    },
    'telephone.telephone': {
      isRequired: { value: true, message: MUST_NOT_BE_EMPTY_ERROR },
    },
    website: {
      isRequired: { value: true, message: MUST_NOT_BE_EMPTY_ERROR },
    },
  };

  const [serverError, setServerError] = useState(false);
  const [formSubmitInProgress, setFormSubmitInProgress] = useState(false);
  const {
    clearFormError,
    deregisterValidation,
    errors,
    formValues,
    getFormValidation,
    handleBlur,
    handleChange,
    handleSubmit,
    hasFormError,
    hasValidation,
    registerValidation,
    setFormError,
    setFormValue,
  } = useForm<RegistrationForm, FormValidations>(data, validations, {
    validateOnChange: true,
  });
  const [pageIndex, setPageIndex] = useState(section);
  const formRef = useRef<HTMLFormElement>(null);

  const trackFormAbandonment = useCallback(() => {
    const finishedWithForm = pageIndex === LAST_PAGE;
    if (!finishedWithForm) {
      track.event({ action: 'Form Abandon', label: `Section ${pageIndex + 1}` });
    }
  }, [pageIndex]);

  useEvent('beforeunload', trackFormAbandonment, window);

  const paginate = (direction: number) => {
    window.scrollTo({ top: 0 });
    setPageIndex(pageIndex + direction);

    return true;
  };

  const handleFormSubmit = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e.preventDefault();

    if (!formSubmitInProgress) {
      setFormSubmitInProgress(true);
      handleSubmit(
        e,
        async () => {
          try {
            await submitOnboardingApplication(formValues);
          } catch (err) {
            logError('Application form submit error:', err as Record<string, unknown>);
            setServerError(true);
          } finally {
            handlePageForward('Form Submit');
            setFormSubmitInProgress(false);
          }
        },
        () => setFormSubmitInProgress(false),
      );
    }
  };

  const handlePageForward = (eventAction: string) => {
    track.event({ action: eventAction, label: `Section ${pageIndex + 1}` });
    return paginate(1);
  };

  const handlePageBack = (eventAction: string) => {
    track.event({ action: eventAction, label: `Section ${pageIndex + 1}` });
    return paginate(-1);
  };

  return (
    <ApplyToSellWithUsContainer>
      <form
        aria-label="registration-form"
        data-dd-privacy="mask"
        data-testid="registration-form"
        noValidate
        onSubmit={(e) => e.preventDefault()}
        ref={formRef}
        style={formSubmitInProgress ? { pointerEvents: 'none' } : {}}
      >
        {pageIndex === 0 && (
          <AboutYou
            clearError={clearFormError}
            deregisterValidation={deregisterValidation}
            errors={errors}
            formValidator={getFormValidation}
            formValues={formValues}
            hasValidation={hasValidation}
            onBlur={handleBlur}
            onFormChange={handleChange}
            pageForward={() => handlePageForward('Next Section Click')}
            registerValidation={registerValidation}
            setFormValue={setFormValue}
          />
        )}
        {pageIndex === 1 && (
          <AboutYourBusiness
            clearError={clearFormError}
            deregisterValidation={deregisterValidation}
            errors={errors}
            formValidator={getFormValidation}
            formValues={formValues}
            hasError={hasFormError}
            onBlur={handleBlur}
            onFormChange={handleChange}
            pageBack={() => handlePageBack('Previous Section Click')}
            pageForward={() => handlePageForward('Next Section Click')}
            registerValidation={registerValidation}
            setError={setFormError}
            setFormValue={setFormValue}
          />
        )}
        {pageIndex === 2 && (
          <AboutYourProducts
            deregisterValidation={deregisterValidation}
            errors={errors}
            formValidator={getFormValidation}
            formValues={formValues}
            handleSubmit={handleFormSubmit}
            hasValidation={hasValidation}
            onBlur={handleBlur}
            onFormChange={handleChange}
            pageBack={() => handlePageBack('Previous Section Click')}
            registerValidation={registerValidation}
            setError={setFormError}
            setFormValue={setFormValue}
          />
        )}
        {pageIndex === 3 && serverError && <ApplicationError />}
        {pageIndex === 3 && !serverError && <ApplicationSubmitted />}
      </form>
    </ApplyToSellWithUsContainer>
  );
};
